import React from 'react';
import { Modal, Form, Input } from 'antd';
import { PostFolder } from '../../../../../../../types/postFolders';
import './style.css';

interface RenameFolderModalProps {
  open: boolean;
  folder: PostFolder;
  onCancel: () => void;
  onSubmit: (values: { name: string }) => Promise<boolean>;
  loading?: boolean;
}

interface ValidationError {
  errorFields?: {
    name: string[];
    errors: string[];
  }[];
}

const RenameFolderModal: React.FC<RenameFolderModalProps> = ({
  open,
  folder,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open && folder) {
      form.setFieldsValue({ name: folder.name });
    }
  }, [open, folder, form]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      const response = await onSubmit(values);
      // Only reset form after successful submission
      if (response) {
        form.resetFields();
      }
    } catch (error: unknown) {
      setSubmitting(false);
      // If it's a validation error, form will handle it
      // If it's an API error, keep the form values
      const validationError = error as ValidationError;
      if (validationError.errorFields) {
        // This is a form validation error, no need to handle
        return;
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Rename Folder"
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={submitting}
      centered
      maskClosable={false}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
      >
        <Form.Item
          name="name"
          label="Folder name"
          rules={[
            { required: true, message: 'Please enter folder name' },
            { pattern: /^[a-zA-Z0-9\s_-]+$/, message: 'Only letters, numbers, spaces, hyphens and underscores allowed' },
            { max: 50, message: 'Folder name cannot be longer than 50 characters' }
          ]}
        >
          <Input 
            placeholder="Enter folder name"
            autoFocus
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RenameFolderModal; 